/**
 * PAPPO — Theme Tokens
 * Defines the 7 themes: Light, Dark Graphite, Neon Purple, Deep Space Cyber,
 * Vermelho Escuro, Vermelho Claro, Azul Gélido.
 * Each theme exports CSS variable values applied via ThemeContext.
 */

export interface ThemeTokens {
  name: string;
  label: string;
  emoji: string;
  bgPrimary: string;
  bgSecondary: string;
  bgTertiary: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  accentPrimary: string;
  accentHover: string;
  accentGlow: string;
  borderColor: string;
  surface: string;
  sidebarBg: string;
  navbarBg: string;
  msgSentBg: string;
  msgSentText: string;
  msgReceivedBg: string;
  msgReceivedText: string;
  shadowSm: string;
  shadowMd: string;
  shadowLg: string;
  inputBg: string;
  cardBg: string;
  cardBorder: string;
  scrollbarThumb: string;
  selectionBg: string;
  selectionText: string;
  /* Superfícies das páginas de autenticação (antes hardcoded no estilo "Deep Space Cyber") */
  authPageBg: string;
  authDot: string;
  authCardBg: string;
  authCardBorder: string;
  authCardShadow: string;
  authLogoGradient: string;
  authAccentText: string;
  authLinkBg: string;
  authLinkBorder: string;
  authGlow: string;
  authLinkHoverBg: string;
  authLinkHoverBorder: string;
}

export const lightTheme: ThemeTokens = {
  name: 'light',
  label: 'Light',
  emoji: '☀️',
  bgPrimary: '#F8FAFC',
  bgSecondary: '#FFFFFF',
  bgTertiary: '#F1F5F9',
  textPrimary: '#1E293B',
  textSecondary: '#475569',
  textMuted: '#64748B',
  accentPrimary: '#4F46E5',
  accentHover: '#4338CA',
  accentGlow: 'rgba(79, 70, 229, 0.15)',
  borderColor: '#E2E8F0',
  surface: '#FFFFFF',
  sidebarBg: '#FFFFFF',
  navbarBg: 'rgba(255, 255, 255, 0.85)',
  msgSentBg: '#4F46E5',
  msgSentText: '#FFFFFF',
  msgReceivedBg: '#F1F5F9',
  msgReceivedText: '#1E293B',
  shadowSm: '0 1px 2px rgba(0,0,0,0.05)',
  shadowMd: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
  shadowLg: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)',
  inputBg: '#FFFFFF',
  cardBg: '#FFFFFF',
  cardBorder: '#E2E8F0',
  scrollbarThumb: '#CBD5E1',
  selectionBg: '#E0E7FF',
  selectionText: '#3730A3',
  authPageBg:
    'radial-gradient(circle at 15% 50%, rgba(199,210,254,0.5), transparent 50%), radial-gradient(circle at 85% 30%, rgba(224,231,255,0.6), transparent 50%), linear-gradient(135deg, #F8FAFC 0%, #EEF2FF 60%, #E0E7FF 100%)',
  authDot: 'rgba(79,70,229,0.04)',
  authCardBg: 'rgba(255,255,255,0.85)',
  authCardBorder: 'rgba(79,70,229,0.15)',
  authCardShadow: '0 24px 64px rgba(30,41,59,0.12), 0 0 0 1px rgba(79,70,229,0.04)',
  authLogoGradient: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
  authAccentText: '#4F46E5',
  authLinkBg: 'rgba(79,70,229,0.05)',
  authLinkBorder: 'rgba(79,70,229,0.2)',
  authGlow: 'rgba(79,70,229,0.25)',
  authLinkHoverBg: 'rgba(79,70,229,0.12)',
  authLinkHoverBorder: 'rgba(79,70,229,0.5)',
};

export const darkGraphiteTheme: ThemeTokens = {
  name: 'dark-graphite',
  label: 'Dark Graphite',
  emoji: '🌑',
  bgPrimary: '#111315',
  bgSecondary: '#1A1D21',
  bgTertiary: '#24282D',
  textPrimary: '#FFFFFF',
  textSecondary: '#A1A1AA',
  textMuted: '#71717A',
  accentPrimary: '#7C3AED',
  accentHover: '#8B5CF6',
  accentGlow: 'rgba(124, 58, 237, 0.25)',
  borderColor: '#2A2F35',
  surface: '#1A1D21',
  sidebarBg: '#1A1D21',
  navbarBg: 'rgba(17, 19, 21, 0.85)',
  msgSentBg: 'linear-gradient(135deg, #7C3AED, #6D28D9)',
  msgSentText: '#FFFFFF',
  msgReceivedBg: '#24282D',
  msgReceivedText: '#FFFFFF',
  shadowSm: '0 1px 2px rgba(0,0,0,0.3)',
  shadowMd: '0 4px 6px -1px rgba(0,0,0,0.4), 0 2px 4px -2px rgba(0,0,0,0.3)',
  shadowLg: '0 10px 15px -3px rgba(0,0,0,0.5), 0 4px 6px -4px rgba(0,0,0,0.4)',
  inputBg: '#24282D',
  cardBg: '#1A1D21',
  cardBorder: '#2A2F35',
  scrollbarThumb: '#3A3F45',
  selectionBg: '#7C3AED',
  selectionText: '#FFFFFF',
  authPageBg:
    'radial-gradient(circle at 15% 50%, rgba(124,58,237,0.18), transparent 50%), radial-gradient(circle at 85% 30%, rgba(60,60,70,0.4), transparent 50%), linear-gradient(135deg, #0B0D0F 0%, #131518 60%, #1A1D21 100%)',
  authDot: 'rgba(124,58,237,0.04)',
  authCardBg: 'rgba(26,29,33,0.7)',
  authCardBorder: 'rgba(124,58,237,0.2)',
  authCardShadow: '0 24px 64px rgba(0,0,0,0.5), 0 0 60px rgba(124,58,237,0.1)',
  authLogoGradient: 'linear-gradient(135deg, #C4B5FD, #7C3AED)',
  authAccentText: '#A78BFA',
  authLinkBg: 'rgba(124,58,237,0.08)',
  authLinkBorder: 'rgba(124,58,237,0.3)',
  authGlow: 'rgba(124,58,237,0.35)',
  authLinkHoverBg: 'rgba(124,58,237,0.18)',
  authLinkHoverBorder: 'rgba(124,58,237,0.7)',
};

export const neonPurpleTheme: ThemeTokens = {
  name: 'neon-purple',
  label: 'Neon Purple',
  emoji: '💜',
  bgPrimary: '#0D0D0F',
  bgSecondary: '#15151A',
  bgTertiary: '#1D1D25',
  textPrimary: '#FFFFFF',
  textSecondary: '#BDBDC7',
  textMuted: '#8888A0',
  accentPrimary: '#A855F7',
  accentHover: '#C084FC',
  accentGlow: 'rgba(168, 85, 247, 0.4)',
  borderColor: 'rgba(255, 255, 255, 0.08)',
  surface: '#15151A',
  sidebarBg: '#15151A',
  navbarBg: 'rgba(13, 13, 15, 0.75)',
  msgSentBg: 'linear-gradient(135deg, #A855F7, #7C3AED)',
  msgSentText: '#FFFFFF',
  msgReceivedBg: '#1D1D25',
  msgReceivedText: '#FFFFFF',
  shadowSm: '0 1px 3px rgba(0,0,0,0.4)',
  shadowMd: '0 0 10px rgba(168,85,247,0.15), 0 4px 6px rgba(0,0,0,0.3)',
  shadowLg: '0 0 25px rgba(168,85,247,0.2), 0 10px 15px rgba(0,0,0,0.4)',
  inputBg: '#1D1D25',
  cardBg: 'rgba(255, 255, 255, 0.03)',
  cardBorder: 'rgba(255, 255, 255, 0.08)',
  scrollbarThumb: '#2D2D3A',
  selectionBg: '#A855F7',
  selectionText: '#FFFFFF',
  authPageBg:
    'radial-gradient(circle at 15% 50%, rgba(168,85,247,0.25), transparent 50%), radial-gradient(circle at 85% 30%, rgba(124,58,237,0.25), transparent 50%), linear-gradient(135deg, #0D0D0F 0%, #15151A 60%, #1D1D25 100%)',
  authDot: 'rgba(168,85,247,0.04)',
  authCardBg: 'rgba(255,255,255,0.03)',
  authCardBorder: 'rgba(168,85,247,0.25)',
  authCardShadow: '0 24px 64px rgba(0,0,0,0.5), 0 0 80px rgba(168,85,247,0.18)',
  authLogoGradient: 'linear-gradient(135deg, #E9D5FF, #A855F7)',
  authAccentText: '#C084FC',
  authLinkBg: 'rgba(168,85,247,0.06)',
  authLinkBorder: 'rgba(168,85,247,0.3)',
  authGlow: 'rgba(168,85,247,0.4)',
  authLinkHoverBg: 'rgba(168,85,247,0.15)',
  authLinkHoverBorder: 'rgba(168,85,247,0.8)',
};

/* Deep Space Cyber — antes era hardcoded nas páginas de auth; agora é um tema selecionável. */
export const deepSpaceCyberTheme: ThemeTokens = {
  name: 'cyber',
  label: 'Deep Space Cyber',
  emoji: '🚀',
  bgPrimary: '#060110',
  bgSecondary: '#0E0420',
  bgTertiary: '#15032D',
  textPrimary: '#FFFFFF',
  textSecondary: '#C4B5FD',
  textMuted: '#8B7BB0',
  accentPrimary: '#A78BFA',
  accentHover: '#C4B5FD',
  accentGlow: 'rgba(139,92,246,0.4)',
  borderColor: 'rgba(167,139,250,0.18)',
  surface: '#0E0420',
  sidebarBg: '#0A0218',
  navbarBg: 'rgba(6,1,16,0.75)',
  msgSentBg: 'linear-gradient(135deg, #A78BFA, #7C3AED)',
  msgSentText: '#FFFFFF',
  msgReceivedBg: 'rgba(20,10,40,0.6)',
  msgReceivedText: '#FFFFFF',
  shadowSm: '0 1px 3px rgba(0,0,0,0.5)',
  shadowMd: '0 0 12px rgba(139,92,246,0.18), 0 4px 6px rgba(0,0,0,0.4)',
  shadowLg: '0 0 32px rgba(139,92,246,0.22), 0 24px 64px rgba(0,0,0,0.6)',
  inputBg: 'rgba(20,10,40,0.5)',
  cardBg: 'rgba(20,10,40,0.5)',
  cardBorder: 'rgba(167,139,250,0.25)',
  scrollbarThumb: '#2E0854',
  selectionBg: '#7C3AED',
  selectionText: '#FFFFFF',
  authPageBg:
    'radial-gradient(circle at 15% 50%, rgba(30,5,60,0.4), transparent 50%), radial-gradient(circle at 85% 30%, rgba(80,10,110,0.4), transparent 50%), linear-gradient(135deg, #020005 0%, #060110 30%, #15032D 70%, #2E0854 100%)',
  authDot: 'rgba(167,139,250,0.03)',
  authCardBg: 'rgba(20,10,40,0.5)',
  authCardBorder: 'rgba(167,139,250,0.25)',
  authCardShadow: '0 24px 64px rgba(0,0,0,0.6), 0 0 80px rgba(139,92,246,0.15)',
  authLogoGradient: 'linear-gradient(135deg, #EADDFF, #A78BFA)',
  authAccentText: '#C4B5FD',
  authLinkBg: 'rgba(139,92,246,0.05)',
  authLinkBorder: 'rgba(167,139,250,0.3)',
  authGlow: 'rgba(139,92,246,0.4)',
  authLinkHoverBg: 'rgba(139,92,246,0.15)',
  authLinkHoverBorder: 'rgba(167,139,250,0.8)',
};

/* Vermelho Escuro — quase preto com brasa vermelha; o mais escuro do par. */
export const darkRedTheme: ThemeTokens = {
  name: 'dark-red',
  label: 'Vermelho Escuro',
  emoji: '🩸',
  bgPrimary: '#0B0707',
  bgSecondary: '#140E0E',
  bgTertiary: '#1D1414',
  textPrimary: '#FFFFFF',
  textSecondary: '#D9A9A9',
  textMuted: '#9A6E6E',
  accentPrimary: '#DC2626',
  accentHover: '#EF4444',
  accentGlow: 'rgba(220, 38, 38, 0.30)',
  borderColor: '#2A1C1C',
  surface: '#140E0E',
  sidebarBg: '#0A0606',
  navbarBg: 'rgba(11, 7, 7, 0.88)',
  msgSentBg: 'linear-gradient(135deg, #DC2626, #7F1414)',
  msgSentText: '#FFFFFF',
  msgReceivedBg: '#1D1414',
  msgReceivedText: '#FFFFFF',
  shadowSm: '0 1px 2px rgba(0,0,0,0.5)',
  shadowMd: '0 0 10px rgba(220,38,38,0.14), 0 4px 6px rgba(0,0,0,0.5)',
  shadowLg: '0 0 28px rgba(220,38,38,0.20), 0 10px 15px rgba(0,0,0,0.6)',
  inputBg: '#1D1414',
  cardBg: '#140E0E',
  cardBorder: '#2A1C1C',
  scrollbarThumb: '#3A2424',
  selectionBg: '#DC2626',
  selectionText: '#FFFFFF',
  authPageBg:
    'radial-gradient(circle at 15% 50%, rgba(220,38,38,0.16), transparent 50%), radial-gradient(circle at 85% 30%, rgba(60,12,12,0.55), transparent 50%), linear-gradient(135deg, #050303 0%, #0B0707 60%, #140E0E 100%)',
  authDot: 'rgba(220,38,38,0.04)',
  authCardBg: 'rgba(20,14,14,0.75)',
  authCardBorder: 'rgba(220,38,38,0.22)',
  authCardShadow: '0 24px 64px rgba(0,0,0,0.6), 0 0 60px rgba(220,38,38,0.12)',
  authLogoGradient: 'linear-gradient(135deg, #FCA5A5, #DC2626)',
  authAccentText: '#F87171',
  authLinkBg: 'rgba(220,38,38,0.08)',
  authLinkBorder: 'rgba(220,38,38,0.3)',
  authGlow: 'rgba(220,38,38,0.38)',
  authLinkHoverBg: 'rgba(220,38,38,0.18)',
  authLinkHoverBorder: 'rgba(220,38,38,0.7)',
};

/* Vermelho Claro — dark quente (não-branco), mais claro que o Escuro; acento rubro vivo. */
export const lightRedTheme: ThemeTokens = {
  name: 'light-red',
  label: 'Vermelho Claro',
  emoji: '🌹',
  bgPrimary: '#241619',
  bgSecondary: '#2E1E22',
  bgTertiary: '#3A262B',
  textPrimary: '#FFF5F5',
  textSecondary: '#D8B4B4',
  textMuted: '#A88585',
  accentPrimary: '#F87171',
  accentHover: '#FCA5A5',
  accentGlow: 'rgba(248, 113, 113, 0.28)',
  borderColor: '#4A3237',
  surface: '#2E1E22',
  sidebarBg: '#201316',
  navbarBg: 'rgba(36, 22, 25, 0.85)',
  msgSentBg: 'linear-gradient(135deg, #F87171, #DC2626)',
  msgSentText: '#FFFFFF',
  msgReceivedBg: '#3A262B',
  msgReceivedText: '#FFF5F5',
  shadowSm: '0 1px 2px rgba(0,0,0,0.4)',
  shadowMd: '0 0 10px rgba(248,113,113,0.14), 0 4px 6px rgba(0,0,0,0.4)',
  shadowLg: '0 0 28px rgba(248,113,113,0.20), 0 10px 15px rgba(0,0,0,0.5)',
  inputBg: '#3A262B',
  cardBg: '#2E1E22',
  cardBorder: '#4A3237',
  scrollbarThumb: '#5A3E43',
  selectionBg: '#F87171',
  selectionText: '#2A0E0E',
  authPageBg:
    'radial-gradient(circle at 15% 50%, rgba(248,113,113,0.18), transparent 50%), radial-gradient(circle at 85% 30%, rgba(120,40,45,0.5), transparent 50%), linear-gradient(135deg, #180E10 0%, #241619 60%, #2E1E22 100%)',
  authDot: 'rgba(248,113,113,0.04)',
  authCardBg: 'rgba(46,30,34,0.7)',
  authCardBorder: 'rgba(248,113,113,0.25)',
  authCardShadow: '0 24px 64px rgba(0,0,0,0.5), 0 0 60px rgba(248,113,113,0.12)',
  authLogoGradient: 'linear-gradient(135deg, #FECACA, #F87171)',
  authAccentText: '#FCA5A5',
  authLinkBg: 'rgba(248,113,113,0.08)',
  authLinkBorder: 'rgba(248,113,113,0.3)',
  authGlow: 'rgba(248,113,113,0.38)',
  authLinkHoverBg: 'rgba(248,113,113,0.18)',
  authLinkHoverBorder: 'rgba(248,113,113,0.7)',
};

/* Azul Gélido — escuro, nebuloso e frio, com brilho azul-gelo. */
export const iceBlueTheme: ThemeTokens = {
  name: 'ice-blue',
  label: 'Azul Gélido',
  emoji: '🧊',
  bgPrimary: '#060A12',
  bgSecondary: '#0B1220',
  bgTertiary: '#111C2E',
  textPrimary: '#EAF3FF',
  textSecondary: '#9FC1E0',
  textMuted: '#6B87A6',
  accentPrimary: '#38BDF8',
  accentHover: '#7DD3FC',
  accentGlow: 'rgba(56, 189, 248, 0.32)',
  borderColor: 'rgba(125, 211, 252, 0.16)',
  surface: '#0B1220',
  sidebarBg: '#080F1A',
  navbarBg: 'rgba(6, 10, 18, 0.75)',
  msgSentBg: 'linear-gradient(135deg, #38BDF8, #2563EB)',
  msgSentText: '#FFFFFF',
  msgReceivedBg: 'rgba(17, 28, 46, 0.6)',
  msgReceivedText: '#EAF3FF',
  shadowSm: '0 1px 3px rgba(0,0,0,0.5)',
  shadowMd: '0 0 12px rgba(56,189,248,0.16), 0 4px 6px rgba(0,0,0,0.4)',
  shadowLg: '0 0 32px rgba(56,189,248,0.2), 0 24px 64px rgba(0,0,0,0.6)',
  inputBg: 'rgba(17, 28, 46, 0.5)',
  cardBg: 'rgba(17, 28, 46, 0.5)',
  cardBorder: 'rgba(125, 211, 252, 0.2)',
  scrollbarThumb: '#1E3A5F',
  selectionBg: '#2563EB',
  selectionText: '#FFFFFF',
  authPageBg:
    'radial-gradient(circle at 15% 50%, rgba(56,189,248,0.18), transparent 50%), radial-gradient(circle at 85% 30%, rgba(37,99,235,0.28), transparent 50%), linear-gradient(135deg, #02040A 0%, #060A12 30%, #0B1524 70%, #10233F 100%)',
  authDot: 'rgba(125,211,252,0.03)',
  authCardBg: 'rgba(17,28,46,0.5)',
  authCardBorder: 'rgba(125,211,252,0.22)',
  authCardShadow: '0 24px 64px rgba(0,0,0,0.6), 0 0 80px rgba(56,189,248,0.14)',
  authLogoGradient: 'linear-gradient(135deg, #E0F2FE, #38BDF8)',
  authAccentText: '#7DD3FC',
  authLinkBg: 'rgba(56,189,248,0.05)',
  authLinkBorder: 'rgba(125,211,252,0.3)',
  authGlow: 'rgba(56,189,248,0.4)',
  authLinkHoverBg: 'rgba(56,189,248,0.15)',
  authLinkHoverBorder: 'rgba(125,211,252,0.8)',
};

export const themes = {
  light: lightTheme,
  'dark-graphite': darkGraphiteTheme,
  'neon-purple': neonPurpleTheme,
  cyber: deepSpaceCyberTheme,
  'dark-red': darkRedTheme,
  'light-red': lightRedTheme,
  'ice-blue': iceBlueTheme,
} as const;

export type ThemeName = keyof typeof themes;
